import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react'

interface User {
  email: string
  brandId: string
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

// Determine the API base URL
const getApiBaseUrl = () => {
  // Use the same hostname as the current page if possible
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // For local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000/api';
    }
    // For production domain with HTTPS
    if (hostname === 'tu-du-dum.com' || hostname === 'www.tu-du-dum.com') {
      return 'https://tu-du-dum.com/api';
    }
    // For direct IP access (current production) - use HTTPS for IP too
    if (hostname === '195.24.67.210') {
      return 'https://tu-du-dum.com/api';
    }
  }
  // Default fallback to the domain with HTTPS
  return 'https://tu-du-dum.com/api';
};

const API_BASE_URL = getApiBaseUrl();

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/check-session`, {
          credentials: 'include'
        })
        if (response.ok) {
          const data = await response.json()
          setUser(data.user)
        }
      } catch (error) {
        console.error('Session check failed:', error)
      } finally {
        setIsLoading(false)
      }
    }

    checkSession()
  }, [])

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      setUser(data.user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await fetch(`${API_BASE_URL}/logout`, { 
      method: 'POST',
      credentials: 'include'
    })
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 